import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  Button,
  Spinner,
  HStack,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
} from '@chakra-ui/react';
import {
  MdPlayArrow,
  MdPause,
  MdVolumeUp,
  MdVolumeOff,
  MdFullscreen,
  MdFullscreenExit,
  MdOpenInNew,
  MdPictureInPictureAlt,
  MdClosedCaption,
  MdAirplay,
  MdCast,
} from 'react-icons/md';
import Hls from 'hls.js';
import type { PlayableItem } from '../types';
import { streamGhostOnDarkHover, streamOutlineOnDarkHover } from '../darkThemeButtonProps';
import {
  openWebKitAirPlayPicker,
  supportsWebKitAirPlayPicker,
  userAgentSuggestsAppleAirPlayContext,
  webkitAirPlayPickerSupportedByEngine,
  type VideoWithAirPlay,
} from '../casting/airPlay';
import { resolvePlaybackStreamUrl } from '../casting/playbackUrl';
import { useChromecast } from '../hooks/useChromecast';
import { tryHandleHlsFatal, usePlayerErrorHandler, userMessageForHlsFatal } from '../hooks/usePlayerErrorHandler';
import './playerShellFullscreen.css';

interface Props {
  item: PlayableItem | null;
  mini: boolean;
  onToggleMini: () => void;
  onClose?: () => void;
  autoplay: boolean;
  onProgress?: (position: number, duration: number) => void;
  /** Right-column layout: cap video height */
  embedded?: boolean;
  videoMaxH?: string;
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

type FsDoc = Document & {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
  mozCancelFullScreen?: () => void;
  msExitFullscreen?: () => void;
};

type FsHTMLElement = HTMLElement & {
  webkitRequestFullscreen?: () => void;
  mozRequestFullScreen?: () => void;
  msRequestFullscreen?: () => void;
};

interface CaptionChoice {
  id: string;
  label: string;
}

const CAPTION_OFF: CaptionChoice = { id: 'off', label: 'Off' };

function buildNativeCaptionChoices(video: HTMLVideoElement): CaptionChoice[] {
  const list: CaptionChoice[] = [CAPTION_OFF];
  for (let i = 0; i < video.textTracks.length; i += 1) {
    const t = video.textTracks[i];
    if (t.kind !== 'subtitles' && t.kind !== 'captions') continue;
    list.push({
      id: `native:${i}`,
      label: t.label || t.language || `Captions ${list.length}`,
    });
  }
  return list;
}

function buildHlsCaptionChoices(hls: Hls): CaptionChoice[] {
  const list: CaptionChoice[] = [CAPTION_OFF];
  const tracks = hls.subtitleTracks ?? [];
  tracks.forEach((t, i) => {
    const lang = 'lang' in t && t.lang ? t.lang : (t as { language?: string }).language;
    list.push({
      id: `hls:${i}`,
      label: t.name || lang || `Track ${i + 1}`,
    });
  });
  return list;
}

function getFullscreenElement(): Element | null {
  const d = document as FsDoc;
  return (
    document.fullscreenElement ??
    d.webkitFullscreenElement ??
    d.mozFullScreenElement ??
    d.msFullscreenElement ??
    null
  );
}

type VideoFullscreen = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  /** Safari: true while video is using native fullscreen (no document.fullscreenElement). */
  webkitDisplayingFullscreen?: boolean;
};

/** iPhone / iPod — use native video fullscreen (custom chrome + element FS is unreliable). */
function isIphoneOrIpod(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPod/i.test(navigator.userAgent);
}

/** Safari / WebKit (not Chrome, Edge, Opera, or Android WebView). */
function isSafariUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/(Chrome|Chromium|Edg|OPR|Android)/i.test(ua);
}

/** iPad (incl. iPadOS desktop UA) */
function isIpad(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iPad/i.test(navigator.userAgent)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/** True when document-level fullscreen is our shell or video (or a descendant of the shell). */
function isDocumentFullscreenForPlayer(container: HTMLElement | null, video: HTMLVideoElement | null): boolean {
  const fs = getFullscreenElement();
  if (!fs) return false;
  if (video && fs === video) return true;
  if (container && (fs === container || container.contains(fs))) return true;
  return false;
}

/**
 * Vendor-prefixed element fullscreen (sync — keeps user-activation where the standard API returns a Promise).
 * Order: W3C → webkit → moz → ms
 */
function requestFullscreenVendorChain(el: HTMLElement): boolean {
  const node = el as FsHTMLElement;

  if (typeof node.requestFullscreen === 'function') {
    try {
      /* Bare call only — `navigationUI` rejects on some builds and the .catch microtask can miss user-gesture. */
      const p = node.requestFullscreen();
      if (p !== undefined && typeof (p as Promise<void>).then === 'function') {
        void (p as Promise<void>).catch(() => requestFullscreenVendorChainLegacy(node));
      }
      return true;
    } catch {
      return requestFullscreenVendorChainLegacy(node);
    }
  }
  return requestFullscreenVendorChainLegacy(node);
}

function requestFullscreenVendorChainLegacy(node: FsHTMLElement): boolean {
  try {
    if (typeof node.webkitRequestFullscreen === 'function') {
      node.webkitRequestFullscreen();
      return true;
    }
  } catch {
    /* continue */
  }
  try {
    if (typeof node.mozRequestFullScreen === 'function') {
      node.mozRequestFullScreen();
      return true;
    }
  } catch {
    /* continue */
  }
  try {
    if (typeof node.msRequestFullscreen === 'function') {
      node.msRequestFullscreen();
      return true;
    }
  } catch {
    /* continue */
  }
  return false;
}

/**
 * Enter fullscreen from a **direct user gesture** (click / pointer / keydown only).
 * - iPhone/iPod: `video.webkitEnterFullscreen()` (HLS via MSE or native src both attach to the same `<video>`).
 * - iPad + desktop: player **container** first (custom controls), then `<video>`, then WebKit video native.
 */
function enterFullscreenPlayer(container: HTMLElement, video: HTMLVideoElement | null): void {
  const v = video as VideoFullscreen | null;

  if (isIphoneOrIpod() && v && typeof v.webkitEnterFullscreen === 'function') {
    try {
      v.webkitEnterFullscreen();
      return;
    } catch {
      /* fall through */
    }
  }

  const tryVideoElementDocumentFs = (): boolean => {
    if (!v) return false;
    return requestFullscreenVendorChain(v);
  };

  const tryVideoWebkitNative = (): void => {
    if (v && typeof v.webkitEnterFullscreen === 'function') {
      try {
        v.webkitEnterFullscreen();
      } catch {
        /* ignore */
      }
    }
  };

  /* iPad: prefer container (Split View friendly); iPhone path already returned. */
  if (isIpad()) {
    if (requestFullscreenVendorChain(container)) return;
    if (tryVideoElementDocumentFs()) return;
    tryVideoWebkitNative();
    return;
  }

  /* Desktop / Android: container keeps custom UI in fullscreen. */
  if (requestFullscreenVendorChain(container)) return;
  if (tryVideoElementDocumentFs()) return;
  tryVideoWebkitNative();
}

/** Exit document/element fullscreen and WebKit video-native fullscreen when applicable. */
function exitFullscreenAny(video: HTMLVideoElement | null): void {
  const d = document as FsDoc;
  const fsEl = getFullscreenElement();
  const docFsWasVideo = Boolean(video && fsEl === video);

  if (document.fullscreenElement) {
    void document.exitFullscreen().catch(() => {});
  } else if (d.webkitFullscreenElement && typeof d.webkitExitFullscreen === 'function') {
    try {
      d.webkitExitFullscreen();
    } catch {
      /* ignore */
    }
  } else if (d.mozFullScreenElement && typeof d.mozCancelFullScreen === 'function') {
    try {
      d.mozCancelFullScreen();
    } catch {
      /* ignore */
    }
  } else if (d.msFullscreenElement && typeof d.msExitFullscreen === 'function') {
    try {
      d.msExitFullscreen();
    } catch {
      /* ignore */
    }
  }

  const vv = video as VideoFullscreen | null;
  if (vv && typeof vv.webkitExitFullscreen === 'function' && (docFsWasVideo || isIphoneOrIpod() || isIpad())) {
    try {
      vv.webkitExitFullscreen();
    } catch {
      /* not in webkit video fullscreen */
    }
  }
}

function PlayerShellComponent({
  item,
  mini,
  onToggleMini,
  onClose,
  autoplay,
  onProgress,
  embedded,
  videoMaxH,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  /** Avoid re-attaching HLS on every parent render (inline onProgress changes identity each render). */
  const onProgressRef = useRef(onProgress);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  /** Latest volume/mute for stream attach (effect must not depend on volume — avoids full teardown on slider move). */
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);
  volumeRef.current = volume;
  mutedRef.current = muted;
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const {
    playbackError,
    clearPlaybackError,
    resetPlaybackRecovery,
    reportVideoMediaError,
    reportPlaybackMessage,
    hlsRecoveryRef,
    devLog,
  } = usePlayerErrorHandler();
  const [fs, setFs] = useState(false);
  const [captionChoices, setCaptionChoices] = useState<CaptionChoice[]>([CAPTION_OFF]);
  const [captionId, setCaptionId] = useState<string>('off');
  /** Instance exposes `webkitShowPlaybackTargetPicker` (may lag behind engine until media attaches). */
  const [airPlayVideoPicker, setAirPlayVideoPicker] = useState(false);
  const airPlayEngineSupported = useMemo(() => webkitAirPlayPickerSupportedByEngine(), []);
  /** WebKit: playback is routed to Apple TV / AirPlay / wireless display. */
  const [airPlayWireless, setAirPlayWireless] = useState(false);

  const onRemotePlayingChange = useCallback((playing: boolean) => {
    setPlaying(playing);
  }, []);

  const { url: playbackUrl, isHls: playbackIsHls } = useMemo(
    () => (item ? resolvePlaybackStreamUrl(item) : { url: '', isHls: false }),
    [item],
  );

  const {
    castAvailable,
    castConnected,
    castConnecting,
    castError,
    requestCast,
    stopCast,
  } = useChromecast({
    videoRef,
    streamUrl: playbackUrl || null,
    title: item?.title ?? '',
    poster: item?.poster,
    isHls: playbackIsHls,
    isLive: Boolean(item?.isLive),
    enabled: Boolean(item?.streamUrl),
    onRemotePlayingChange,
  });

  const applyCaption = useCallback((id: string) => {
    setCaptionId(id);
    const vid = videoRef.current;
    const hls = hlsRef.current;
    if (id === 'off') {
      if (hls) hls.subtitleTrack = -1;
      if (vid) {
        for (let i = 0; i < vid.textTracks.length; i += 1) {
          vid.textTracks[i].mode = 'hidden';
        }
      }
      return;
    }
    if (id.startsWith('hls:') && hls) {
      const idx = Number(id.slice(4));
      if (!Number.isNaN(idx)) hls.subtitleTrack = idx;
      return;
    }
    if (id.startsWith('native:') && vid) {
      const idx = Number(id.slice(7));
      for (let i = 0; i < vid.textTracks.length; i += 1) {
        vid.textTracks[i].mode = i === idx ? 'showing' : 'hidden';
      }
    }
  }, []);

  const teardown = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
      } catch {
        /* ignore */
      }
      for (let i = 0; i < v.textTracks.length; i += 1) {
        v.textTracks[i].mode = 'hidden';
      }
      v.removeAttribute('src');
      v.load();
    }
  }, []);

  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    const v = videoRef.current;
    if (!item?.streamUrl || !v) {
      teardown();
      clearPlaybackError();
      resetPlaybackRecovery();
      setLoading(false);
      setCaptionChoices([CAPTION_OFF]);
      setCaptionId('off');
      setAirPlayVideoPicker(false);
      setAirPlayWireless(false);
      return;
    }

    clearPlaybackError();
    resetPlaybackRecovery();
    setLoading(true);
    setCaptionChoices([CAPTION_OFF]);
    setCaptionId('off');
    setAirPlayWireless(false);
    teardown();

    try {
      v.setAttribute('x-webkit-airplay', 'allow');
      /* Hint for WebKit AirPlay (ignored by engines that do not support it). */
      v.setAttribute('airplay', 'allow');
      /* iOS inline playback + fullscreen from <video> (required for HLS in WKWebView / Safari). */
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
    } catch {
      /* ignore */
    }
    v.playsInline = true;
    v.disableRemotePlayback = false;

    const { url: finalUrl, isHls } = resolvePlaybackStreamUrl(item);
    const playbackUrlForFallback = finalUrl;

    let detachPendingNativeReady: (() => void) | null = null;
    let usedNativeWebKitHls = false;
    let triedHlsJsAfterNativeError = false;

    const tryPlay = () => {
      if (!autoplay) return;
      v.muted = mutedRef.current;
      v.volume = volumeRef.current;
      void v.play().catch(() => {
        /* Autoplay with sound blocked — start muted so playback can begin; user can unmute. */
        v.muted = true;
        setMuted(true);
        void v.play().catch(() => {
          /* still blocked or not ready */
        });
      });
    };

    const refreshAirPlay = () => {
      setAirPlayVideoPicker(supportsWebKitAirPlayPicker(v));
    };

    const syncWirelessTarget = () => {
      const vv = v as VideoWithAirPlay;
      setAirPlayWireless(Boolean(vv.webkitCurrentPlaybackTargetIsWireless));
    };

    const onNativeTextTracksChange = () => {
      if (hlsRef.current) return;
      setCaptionChoices(buildNativeCaptionChoices(v));
    };

    const onTime = () => {
      setCurrent(v.currentTime);
      setDuration(Number.isFinite(v.duration) ? v.duration : 0);
      const cb = onProgressRef.current;
      /* Live: skip parent callback (no continue-watching); avoids pointless ancestor re-renders near resizable layout. */
      if (cb && v.duration && !item.isLive) {
        cb(v.currentTime, v.duration);
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVol = () => {
      setMuted(v.muted);
      setVolume(v.volume);
    };
    const onVideoError = () => {
      setLoading(false);
      const errCode = v.error?.code;
      const nativeErrWorthHlsRetry =
        errCode === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED || errCode === MediaError.MEDIA_ERR_DECODE;
      if (
        !triedHlsJsAfterNativeError &&
        usedNativeWebKitHls &&
        nativeErrWorthHlsRetry &&
        Hls.isSupported() &&
        !hlsRef.current &&
        playbackUrlForFallback
      ) {
        triedHlsJsAfterNativeError = true;
        detachPendingNativeReady?.();
        detachPendingNativeReady = null;
        clearPlaybackError();
        setLoading(true);
        try {
          v.pause();
          v.textTracks.removeEventListener('addtrack', onNativeTextTracksChange);
          v.removeAttribute('src');
          v.load();
        } catch {
          /* ignore */
        }
        startHlsJsPlayback(playbackUrlForFallback);
        return;
      }
      reportVideoMediaError(errCode ?? null);
    };

    const onLoadedDataAirPlay = () => refreshAirPlay();
    const onPlayingAirPlay = () => refreshAirPlay();

    const startHlsJsPlayback = (playUrl: string) => {
      hlsRecoveryRef.current = { networkRetries: 0, didMediaRecover: false };
      const hls = new Hls({
        debug: process.env.NODE_ENV === 'development',
        /* Workers + MSE are a common pain point on Safari; Chrome is fine with workers. */
        enableWorker: !isSafariUA(),
        lowLatencyMode: false,
        renderTextTracksNatively: true,
      });
      hlsRef.current = hls;

      const pushHlsCaptions = () => {
        setCaptionChoices(buildHlsCaptionChoices(hls));
      };

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLoading(false);
        pushHlsCaptions();
        tryPlay();
        refreshAirPlay();
      });
      hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, pushHlsCaptions);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (tryHandleHlsFatal(hls, data, hlsRecoveryRef.current, devLog)) {
          return;
        }
        setLoading(false);
        reportPlaybackMessage(userMessageForHlsFatal(data));
      });
      hls.loadSource(playUrl);
      hls.attachMedia(v);
    };

    const attachPlayback = (url: string, isHls: boolean) => {
      detachPendingNativeReady?.();
      detachPendingNativeReady = null;
      usedNativeWebKitHls = false;

      /*
       * Safari / WebKit: prefer native HLS (`<video src=".m3u8">`). Chrome usually has no native HLS
       * and uses hls.js. Some IPTV feeds play under hls.js but fail with WebKit native — then
       * `onVideoError` runs a one-shot hls.js fallback.
       */
      if (isHls && v.canPlayType('application/vnd.apple.mpegurl')) {
        usedNativeWebKitHls = true;
        v.textTracks.addEventListener('addtrack', onNativeTextTracksChange);
        v.src = url;
        let nativeReadyDone = false;
        const onReadyNative = () => {
          if (nativeReadyDone) return;
          nativeReadyDone = true;
          setLoading(false);
          setCaptionChoices(buildNativeCaptionChoices(v));
          tryPlay();
          refreshAirPlay();
        };
        v.addEventListener('loadedmetadata', onReadyNative, { once: true });
        /* Live / some CDNs: metadata can be late; still clear spinner when playback can start. */
        v.addEventListener('canplay', onReadyNative, { once: true });
        detachPendingNativeReady = () => {
          v.removeEventListener('loadedmetadata', onReadyNative);
          v.removeEventListener('canplay', onReadyNative);
          detachPendingNativeReady = null;
        };
      } else if (isHls && Hls.isSupported()) {
        startHlsJsPlayback(url);
      } else {
        v.textTracks.addEventListener('addtrack', onNativeTextTracksChange);
        v.src = url;
        const onMeta = () => {
          setLoading(false);
          setCaptionChoices(buildNativeCaptionChoices(v));
          tryPlay();
          refreshAirPlay();
        };
        v.addEventListener('loadedmetadata', onMeta, { once: true });
      }
    };

    const wireCommonListeners = () => {
      v.addEventListener('timeupdate', onTime);
      v.addEventListener('play', onPlay);
      v.addEventListener('pause', onPause);
      v.addEventListener('volumechange', onVol);
      v.addEventListener('error', onVideoError);
      v.addEventListener('loadeddata', onLoadedDataAirPlay);
      v.addEventListener('playing', onPlayingAirPlay);
      v.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', syncWirelessTarget);
    };

    attachPlayback(finalUrl, isHls);
    refreshAirPlay();
    syncWirelessTarget();
    wireCommonListeners();
    v.volume = volumeRef.current;
    v.muted = mutedRef.current;

    return () => {
      detachPendingNativeReady?.();
      detachPendingNativeReady = null;
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('volumechange', onVol);
      v.removeEventListener('error', onVideoError);
      v.removeEventListener('loadeddata', onLoadedDataAirPlay);
      v.removeEventListener('playing', onPlayingAirPlay);
      v.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', syncWirelessTarget);
      v.textTracks.removeEventListener('addtrack', onNativeTextTracksChange);
      teardown();
    };
  }, [
    item,
    item?.id,
    item?.streamUrl,
    item?.isLive,
    autoplay,
    teardown,
    clearPlaybackError,
    resetPlaybackRecovery,
    reportVideoMediaError,
    reportPlaybackMessage,
    hlsRecoveryRef,
    devLog,
  ]);

  useEffect(() => {
    if (!captionChoices.some((c) => c.id === captionId)) {
      applyCaption('off');
    }
  }, [captionChoices, captionId, applyCaption]);

  /** Keep element volume aligned with slider state (controlled `muted` lives on <video>). */
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.volume = volume;
  }, [volume]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !item) return;
    const onBegin = () => setFs(true);
    const onEnd = () => setFs(false);
    v.addEventListener('webkitbeginfullscreen', onBegin);
    v.addEventListener('webkitendfullscreen', onEnd);
    return () => {
      v.removeEventListener('webkitbeginfullscreen', onBegin);
      v.removeEventListener('webkitendfullscreen', onEnd);
    };
  }, [item]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!item) return;
      const v = videoRef.current;
      const wrap = wrapRef.current;
      if (!v) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (v.paused) {
          v.volume = volume;
          void v.play().catch(() => {
            v.muted = true;
            setMuted(true);
            void v.play().catch(() => {});
          });
        } else {
          v.pause();
        }
      }
      if (e.key.toLowerCase() === 'm') {
        setMuted((m) => !m);
      }
      if (e.key.toLowerCase() === 'f') {
        if (!wrap) return;
        const docFs = isDocumentFullscreenForPlayer(wrap, v);
        const nativeFs = Boolean((v as VideoFullscreen).webkitDisplayingFullscreen);
        if (docFs || nativeFs || fs) {
          exitFullscreenAny(v);
        } else {
          enterFullscreenPlayer(wrap, v);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, fs, volume]);

  useEffect(() => {
    /**
     * Keep `fs` aligned with the browser — read synchronously on fullscreen events.
     * Deferring with rAF can leave one frame (or more) where `fs` is false while already
     * fullscreen, which reapplies tight `maxHeight` on `<video>` and can break layout / exit.
     */
    const sync = () => {
      const wrap = wrapRef.current;
      const vid = videoRef.current;
      const docFs = isDocumentFullscreenForPlayer(wrap, vid);
      const nativeFs = Boolean(vid && (vid as VideoFullscreen).webkitDisplayingFullscreen);
      setFs(docFs || nativeFs);
    };
    const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'] as const;
    events.forEach((ev) => document.addEventListener(ev, sync as EventListener));
    return () => events.forEach((ev) => document.removeEventListener(ev, sync as EventListener));
  }, []);

  /** After mount / stream changes, re-read fullscreen (covers missed events + Strict dev double-mount). */
  useLayoutEffect(() => {
    if (!item) return;
    const wrap = wrapRef.current;
    const vid = videoRef.current;
    if (!wrap || !vid) return;
    const docFs = isDocumentFullscreenForPlayer(wrap, vid);
    const nativeFs = Boolean((vid as VideoFullscreen).webkitDisplayingFullscreen);
    const next = docFs || nativeFs;
    setFs((prev) => (prev === next ? prev : next));
  }, [item, item?.id, item?.streamUrl]);

  const showAirPlayButton =
    airPlayEngineSupported || airPlayVideoPicker || userAgentSuggestsAppleAirPlayContext();
  const airPlayDisabled = !airPlayEngineSupported && !airPlayVideoPicker;

  if (!item) return null;

  const openNewTab = () => {
    window.open(item.streamUrl, '_blank', 'noopener,noreferrer');
  };

  const videoStyle = fs
    ? ({
        width: '100%',
        height: '100%',
        maxHeight: 'none',
        display: 'block',
        objectFit: 'contain' as const,
      } satisfies React.CSSProperties)
    : {
        width: '100%',
        display: 'block',
        maxHeight: mini ? '200px' : embedded ? videoMaxH || 'min(36vh, 300px)' : 'min(56vh, 520px)',
      };

  const shell = (
    <div
      ref={wrapRef}
      className={mini ? 'player-shell-fs-root player-shell-fs-root--mini' : 'player-shell-fs-root player-shell-fs-root--inline'}
    >
      <Flex
        className="player-shell-header"
        px={embedded ? 2 : 3}
        py={embedded ? 1.5 : 2}
        align="center"
        justify="space-between"
        bg="whiteAlpha.100"
        gap={2}
        flexShrink={0}
      >
        <Text fontSize={embedded ? 'xs' : 'sm'} fontWeight="600" color="white" noOfLines={1} flex={1}>
          {item.title}
        </Text>
        <HStack spacing={1}>
          {item.isLive && (
            <Badge colorScheme="red" variant="solid" fontSize="10px">
              LIVE
            </Badge>
          )}
          <Tooltip label="Mini player: shrink the player into a floating window so you can keep browsing." placement="top" openDelay={350} maxW="280px">
            <Box as="span" display="inline-block">
              <IconButton
                aria-label="Mini player"
                size="sm"
                variant="ghost"
                color="white"
                {...streamGhostOnDarkHover}
                icon={<MdPictureInPictureAlt />}
                onClick={onToggleMini}
              />
            </Box>
          </Tooltip>
          {onClose && (
            <Tooltip label="Stop playback and close the player." placement="top" openDelay={350}>
              <Button size="xs" variant="ghost" color="whiteAlpha.700" {...streamGhostOnDarkHover} onClick={onClose}>
                Close
              </Button>
            </Tooltip>
          )}
        </HStack>
      </Flex>
      <Box
        position="relative"
        bg="black"
        className="player-shell-stage"
        {...(fs
          ? {
              flex: '1 1 0%',
              minH: 0,
              minW: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
          : {})}
      >
        {loading && (
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            bg="blackAlpha.600"
            zIndex={2}
          >
            <Spinner size="lg" color="teal.300" thickness="3px" />
          </Flex>
        )}
        {playbackError && (
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            bg="blackAlpha.800"
            zIndex={3}
            px={4}
            direction="column"
            gap={3}
          >
            <Text fontSize="sm" color="whiteAlpha.800" textAlign="center">
              {playbackError}
            </Text>
            <Tooltip label="Open this stream URL in a new browser tab (useful if the site blocks embedded playback)." placement="top" openDelay={350} maxW="280px">
              <Box display="inline-block">
                <Button size="sm" colorScheme="teal" onClick={openNewTab} leftIcon={<MdOpenInNew />}>
                  Open in new tab
                </Button>
              </Box>
            </Tooltip>
            <Tooltip label="Clear the error and try loading the stream again in this player." placement="top" openDelay={350} maxW="280px">
              <Box display="inline-block">
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="whiteAlpha.300"
                  color="white"
                  {...streamOutlineOnDarkHover}
                  onClick={() => {
                    clearPlaybackError();
                    if (videoRef.current && item.streamUrl) {
                      videoRef.current.load();
                    }
                  }}
                >
                  Retry
                </Button>
              </Box>
            </Tooltip>
          </Flex>
        )}
        <video
          ref={videoRef}
          className="player-shell-video"
          playsInline
          controls={false}
          muted={muted}
          style={videoStyle}
        />
      </Box>
      <Flex
        className="player-shell-controls"
        direction="column"
        gap={2}
        px={3}
        py={3}
        bg="#0d1117"
        borderTopWidth="1px"
        borderColor="whiteAlpha.100"
        flexShrink={0}
      >
        {!item.isLive && duration > 0 && (
          <HStack spacing={3}>
            <Text fontSize="xs" color="whiteAlpha.600" w="40px">
              {formatTime(current)}
            </Text>
            <Tooltip label="Seek: drag to jump to another position in this title (VOD / recordings only)." placement="top" openDelay={350} maxW="280px">
              <Box flex={1} minW={0}>
                <Slider
                  aria-label="progress"
                  value={current}
                  min={0}
                  max={duration || 1}
                  onChange={(val) => {
                    const v = videoRef.current;
                    if (v) v.currentTime = val;
                  }}
                  focusThumbOnChange={false}
                >
                  <SliderTrack bg="whiteAlpha.200">
                    <SliderFilledTrack bg="teal.400" />
                  </SliderTrack>
                  <SliderThumb boxSize={3} />
                </Slider>
              </Box>
            </Tooltip>
            <Text fontSize="xs" color="whiteAlpha.600" w="40px" textAlign="right">
              {formatTime(duration)}
            </Text>
          </HStack>
        )}
        <HStack spacing={2} justify="space-between" align="flex-start" flexWrap="wrap" rowGap={2}>
          <HStack>
            <Tooltip
              label={playing ? 'Pause playback' : 'Play or resume playback (may start muted if the browser blocks sound until you interact).'}
              placement="top"
              openDelay={350}
              maxW="280px"
            >
              <Box as="span" display="inline-block">
                <IconButton
                  aria-label={playing ? 'Pause' : 'Play'}
                  icon={playing ? <MdPause /> : <MdPlayArrow />}
                  onClick={() => {
                    const el = videoRef.current;
                    if (!el) return;
                    if (el.paused) {
                      el.volume = volume;
                      void el.play().catch(() => {
                        /* e.g. not allowed — try muted start */
                        el.muted = true;
                        setMuted(true);
                        void el.play().catch(() => {});
                      });
                    } else {
                      el.pause();
                    }
                  }}
                  colorScheme="teal"
                  variant="solid"
                  borderRadius="full"
                />
              </Box>
            </Tooltip>
            <Tooltip
              label={muted ? 'Unmute: restore audio at the current volume level.' : 'Mute audio (stream keeps playing).'}
              placement="top"
              openDelay={350}
              maxW="260px"
            >
              <Box as="span" display="inline-block">
                <IconButton
                  aria-label={muted ? 'Unmute' : 'Mute'}
                  icon={muted ? <MdVolumeOff /> : <MdVolumeUp />}
                  variant="ghost"
                  color="white"
                  {...streamGhostOnDarkHover}
                  onClick={() => setMuted((m) => !m)}
                />
              </Box>
            </Tooltip>
            <Tooltip label="Volume: drag to change level (all the way left mutes)." placement="top" openDelay={350} maxW="260px">
              <Box w="100px" flexShrink={0}>
                <Slider
                  aria-label="volume"
                  value={muted ? 0 : volume}
                  min={0}
                  max={1}
                  step={0.05}
                  onChange={(val) => {
                    const el = videoRef.current;
                    if (el) {
                      el.volume = val;
                      el.muted = val === 0;
                    }
                    setVolume(val);
                    setMuted(val === 0);
                  }}
                >
                  <SliderTrack bg="whiteAlpha.200">
                    <SliderFilledTrack bg="teal.400" />
                  </SliderTrack>
                  <SliderThumb boxSize={3} />
                </Slider>
              </Box>
            </Tooltip>
          </HStack>
          <HStack>
            <Menu placement="top" closeOnSelect>
              <Tooltip
                label={
                  captionChoices.length <= 1
                    ? 'No subtitle or caption tracks are available for this stream.'
                    : 'Subtitles & closed captions: choose a track or turn captions off.'
                }
                placement="top"
                openDelay={350}
                maxW="280px"
              >
                <Box as="span" display="inline-block">
                  <MenuButton
                    as={IconButton}
                    aria-label="Subtitles and closed captions"
                    icon={<MdClosedCaption />}
                    variant="ghost"
                    color="whiteAlpha.700"
                    isDisabled={captionChoices.length <= 1}
                    {...streamGhostOnDarkHover}
                  />
                </Box>
              </Tooltip>
              <MenuList
                bg="#121822"
                borderColor="whiteAlpha.200"
                zIndex={2000}
                py={1}
                minW="200px"
                maxH="240px"
                overflowY="auto"
              >
                {captionChoices.map((c) => (
                  <MenuItem
                    key={c.id}
                    bg={c.id === captionId ? 'whiteAlpha.200' : 'transparent'}
                    color="white"
                    _hover={{ bg: 'whiteAlpha.100' }}
                    _focus={{ bg: 'whiteAlpha.100' }}
                    onClick={() => applyCaption(c.id)}
                  >
                    {c.id === captionId ? '✓ ' : ''}
                    {c.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {showAirPlayButton && (
              <Tooltip
                label={
                  airPlayDisabled
                    ? 'AirPlay only works in Safari (and other WebKit browsers) on iPhone, iPad, and Mac. This browser cannot open Apple’s AirPlay picker — open the stream in Safari, or use Google Cast if you have a Chromecast.'
                    : airPlayEngineSupported
                      ? 'AirPlay: send video to Apple TV, an AirPlay TV, a Mac as a display, or compatible speakers. Choose your device in the system picker. Works in Safari on Mac, iPhone, and iPad.'
                      : 'AirPlay (WebKit output picker).'
                }
                placement="top"
                openDelay={350}
                maxW="280px"
              >
                <Box as="span" display="inline-block">
                  <IconButton
                    aria-label={
                      airPlayWireless
                        ? 'AirPlay active — click to change output'
                        : airPlayDisabled
                          ? 'AirPlay (not available in this browser — use Safari on Apple devices)'
                          : 'AirPlay — send to Apple TV or another display'
                    }
                    icon={<MdAirplay />}
                    variant="ghost"
                    color={airPlayWireless ? 'teal.300' : 'whiteAlpha.700'}
                    isDisabled={airPlayDisabled}
                    {...streamGhostOnDarkHover}
                    onClick={() => openWebKitAirPlayPicker(videoRef.current)}
                  />
                </Box>
              </Tooltip>
            )}
            {airPlayWireless && (
              <Badge colorScheme="teal" variant="subtle" fontSize="9px" px={1.5} py={0}>
                AirPlay
              </Badge>
            )}
            {castAvailable && (
              <Tooltip
                label={
                  castConnected
                    ? 'Stop casting — playback stays on your Chromecast until you disconnect or pick another source.'
                    : 'Cast to Chromecast / Android TV (Chrome or Edge on desktop; requires a Cast receiver on your network). Uses Google’s default media receiver — HLS and MP4 work when the receiver and stream allow it.'
                }
                placement="top"
                openDelay={350}
                maxW="280px"
              >
                <Box as="span" display="inline-block">
                  <IconButton
                    aria-label={castConnected ? 'Stop Google Cast' : 'Google Cast'}
                    icon={<MdCast />}
                    variant="ghost"
                    color={castConnected ? 'teal.300' : 'whiteAlpha.700'}
                    isDisabled={castConnecting}
                    {...streamGhostOnDarkHover}
                    onClick={() => {
                      if (castConnected) stopCast();
                      else void requestCast();
                    }}
                  />
                </Box>
              </Tooltip>
            )}
            {castConnected && (
              <Badge colorScheme="purple" variant="subtle" fontSize="9px" px={1.5} py={0}>
                Casting
              </Badge>
            )}
            <Tooltip
              label="Open this stream in a new browser tab (same URL as the player)."
              placement="top"
              openDelay={350}
              maxW="260px"
            >
              <Box as="span" display="inline-block">
                <IconButton
                  aria-label="Open in new tab"
                  icon={<MdOpenInNew />}
                  variant="ghost"
                  color="whiteAlpha.700"
                  {...streamGhostOnDarkHover}
                  onClick={openNewTab}
                />
              </Box>
            </Tooltip>
            <Tooltip
              label={
                fs
                  ? 'Exit fullscreen: return to normal layout (or press Esc / F).'
                  : 'Enter fullscreen: only this player (custom controls). On iPhone, may use system video fullscreen.'
              }
              placement="top"
              openDelay={350}
              maxW="280px"
            >
              <Box as="span" display="inline-block">
                <IconButton
                  type="button"
                  aria-label={fs ? 'Exit fullscreen' : 'Enter fullscreen'}
                  icon={fs ? <MdFullscreenExit /> : <MdFullscreen />}
                  variant="ghost"
                  color="whiteAlpha.700"
                  {...streamGhostOnDarkHover}
                  onClick={(e) => {
                    e.preventDefault();
                    const wrap = wrapRef.current;
                    const v = videoRef.current;
                    if (!wrap || !v) return;
                    const docFs = isDocumentFullscreenForPlayer(wrap, v);
                    const nativeFs = Boolean((v as VideoFullscreen).webkitDisplayingFullscreen);
                    if (docFs || nativeFs || fs) {
                      exitFullscreenAny(v);
                    } else {
                      enterFullscreenPlayer(wrap, v);
                    }
                  }}
                />
              </Box>
            </Tooltip>
          </HStack>
        </HStack>
        {castError && (
          <Text fontSize="xs" color="red.300" px={1} noOfLines={3}>
            {castError}
          </Text>
        )}
      </Flex>
    </div>
  );

  /* Plain wrapper (no Chakra/Emotion) so parent re-styles never touch the subtree under element-fullscreen. */
  return (
    <div
      className="player-shell-chrome-root"
      style={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        ...(embedded ? { flex: 1 } : {}),
      }}
    >
      {shell}
    </div>
  );
}

export const PlayerShell = memo(PlayerShellComponent);
